// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { isNativeApp } from "./server";
import { readPlaintextSlots, removePlaintextSlots, writePlaintextSlot, type KeyRecord } from "./keylock";

// Bio-lock: the NATIVE-only fourth at-rest key tier (Phase mobile). It mirrors the passphrase keylock
// but the gate is the OS, not a passphrase — the whole device keyring lives in a single
// `zalmoxis.biolock.<userId>` blob that's AES-GCM-encrypted by an Android Keystore key created with
// setUserAuthenticationRequired(true) + BIOMETRIC_STRONG | DEVICE_CREDENTIAL. So reading the keys
// needs a fingerprint/face OR the device PIN/pattern/password, enforced by hardware — even a rooted
// phone can't open the blob without passing the prompt. This is what the WebAuthn step-up gives the
// desktop/web client; the Capacitor WebView has no WebAuthn/PRF, so this native plugin stands in.
//
// Recovery is unchanged: the encryption key is bound to this device's secure hardware, so if the
// biometrics are reset (KEY_INVALIDATED) the local blob is undecryptable — the server key backup is
// the durable source of truth, exactly like the other tiers.

const BIOLOCK_PREFIX = "zalmoxis.biolock.";
const blobKey = (userId: string): string => BIOLOCK_PREFIX + userId;

// The Biometric plugin lives in the same Capacitor module as UnifiedPush; import it lazily and use it
// INLINE in each function. Never return the plugin proxy through an async function / Promise.resolve:
// the proxy makes every property (including `.then`) a callable, so promise assimilation would invoke
// `Biometric.then(...)`, which the bridge forwards to native as a bogus method ("not implemented").

// After unlocking (or (re)locking) the decrypted records are held in memory for the session, so key
// backup can reach the private keys WITHOUT re-prompting and WITHOUT writing plaintext to storage.
let unlockedRecords: KeyRecord[] | null = null;

// unlockedBioPrivs returns the armored private keys unlocked this session (empty until unlocked).
// Used for key backup when isBioLocked() — the plaintext slots are gone while locked.
export function unlockedBioPrivs(): string[] {
  return (unlockedRecords ?? []).map((r) => r.priv).filter(Boolean);
}

// isBioLocked reports whether THIS USER's keyring is stored behind the device biometric/credential.
export function isBioLocked(userId: string): boolean {
  return localStorage.getItem(blobKey(userId)) !== null;
}

// bioLockAvailable reports whether this device can offer the tier — native app + a usable strong
// biometric or device credential. The Profile panel hides the option when false.
export async function bioLockAvailable(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { Biometric } = await import("capacitor-unifiedpush");
    return (await Biometric.biometricAvailable()).available;
  } catch {
    return false;
  }
}

// enableBioLock moves every one of the user's device-key slots into the biometric-gated blob and
// removes the slots. Prompts for the biometric/credential. Returns how many keys were locked.
export async function enableBioLock(userId: string): Promise<number> {
  const { Biometric } = await import("capacitor-unifiedpush");
  const records = await readPlaintextSlots(userId);
  const { blob } = await Biometric.biometricProtect({ data: JSON.stringify(records) });
  localStorage.setItem(blobKey(userId), blob);
  unlockedRecords = records;
  removePlaintextSlots(userId);
  return records.length;
}

// unlockBio decrypts the user's blob to its key records (prompts for the biometric/credential).
// Throws if the user cancels; rejects with code "KEY_INVALIDATED" if the device biometrics changed.
export async function unlockBio(userId: string): Promise<KeyRecord[]> {
  const blob = localStorage.getItem(blobKey(userId));
  if (!blob) return [];
  const { Biometric } = await import("capacitor-unifiedpush");
  const { data } = await Biometric.biometricUnlock({ blob });
  const records = JSON.parse(data) as KeyRecord[];
  unlockedRecords = records; // keep in memory so backup can reach them while locked
  return records;
}

// relockBio re-encrypts an updated record set into the blob (e.g. after minting a fresh device key
// while locked). Prompts for the biometric/credential.
export async function relockBio(userId: string, records: KeyRecord[]): Promise<void> {
  const { Biometric } = await import("capacitor-unifiedpush");
  const { blob } = await Biometric.biometricProtect({ data: JSON.stringify(records) });
  localStorage.setItem(blobKey(userId), blob);
  unlockedRecords = records;
}

// disableBioLock decrypts the keyring, rewrites the (sealed) plaintext slots, and clears the blob.
// Prompts for the biometric/credential.
export async function disableBioLock(userId: string): Promise<void> {
  const records = await unlockBio(userId);
  for (const r of records) await writePlaintextSlot(r);
  localStorage.removeItem(blobKey(userId));
  unlockedRecords = null; // keys are back in the (sealed) slot store; collectLocalKeyring reads them
}
